import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { DocumentTypeCodes, SortCodeIds, VatCardIds } from '../../Utils/PCN874Constants';
import XtramModal, { XtramButton } from '../Widgets/XtramModal/XtramModal';

const PCNDocumentationModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const [activeTab, setActiveTab] = useState(0);

  const transactionTypes = [
    {
      code: DocumentTypeCodes.REGULAR_SALE,
      name: t('RegularSale'),
      nameHe: 'מכירה רגילה',
      type: 'Sale',
      color: '#28a745',
      description: t('PCNRegularSaleDesc'),
      useWhen: [t('PCNDomesticSale'), t('PCNCustomerVatIdKnown')],
      rules: [t('PCNMandatoryVatIdOver5000'), t('PCNZeroExemptSplit')],
      buildStructure: { sortCodeId: t('PCNIncomeCode'), sortCodeName: 'מכירות', debitAccount: t('PCNClientAccount'), creditAccount: t('PCNIncomeCard'), vat: '100', accountCaseId: t('PCNCustomerCaseId'), amountMeaning: t('PCNGrossAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNClientAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNCustomerVat'),
        [t('PCNInvoiceDate')]: t('PCNInvoiceDateValue'),
        [t('PCNRefGroup')]: t('PCNSeriesOrZeros'),
        [t('PCNRefNumber')]: t('PCNInvoiceNumber'),
        [t('PCNVat')]: t('PCNFullVat'),
        [t('PCNSign')]: '+',
        [t('PCNAmountExclVat')]: t('PCNFullTaxableAmount')
      }
    },
    {
      code: DocumentTypeCodes.PRIVATE_CUSTOMER_SALE,
      name: t('PrivateCustomer'),
      nameHe: 'לקוח פרטי / קופה רושמת',
      type: 'Sale',
      color: '#ffc107',
      description: t('PCNPrivateCustomerDesc'),
      useWhen: [t('PCNCashRegisterSales'), t('PCNManySmallCustomers'), t('PCNAggregatedDailySales')],
      rules: [t('PCNAggregatesSmallSales'), t('PCNMultipleTimesPerMonth')],
      buildStructure: { sortCodeId: t('PCNIncomeCodeWithPCNLOrRegularNoNumber'), sortCodeName: 'מכירות מקופה רושמת', debitAccount: t('PCNPrivateCustomerAccount'), creditAccount: t('PCNIncomeCard'), vat: '100', accountCaseId: t('PCNNone'), amountMeaning: t('PCNAggregatedSales') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNPrivateCustomerAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: '000000000',
        [t('PCNInvoiceDate')]: t('PCNLastDayOfMonth'),
        [t('PCNRefNumber')]: t('PCNNumberOfInvoices'),
        [t('PCNVat')]: t('PCNTotalVat'),
        [t('PCNAmount')]: t('PCNTotalSales')
      }
    },
    {
      code: DocumentTypeCodes.SELF_INVOICE_SALE,
      name: t('SelfInvoiceSale'),
      nameHe: 'חשבונית עצמית - מכירה',
      type: 'Sale',
      color: '#9c27b0',
      description: t('PCNReverseChargeDesc'),
      useWhen: [t('PCNReverseChargeTransactions'), t('PCNSelfBillingArrangements')],
      rules: [t('PCNUsuallyMirroredByC')],
      buildStructure: { sortCodeId: SortCodeIds.SELF_INVOICE_SALE, sortCodeName: 'חשבונית עצמית מכירה', debitAccount: `${VatCardIds.EXPENSE} (${t('PCNExpenseVatCard')})`, creditAccount: `${VatCardIds.INCOME} (${t('PCNIncomeVatCard')})`, vat: '0', accountCaseId: t('PCNSupplierVat'), amountMeaning: t('PCNTheVatAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNSupplierAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNSupplierVat'),
        [t('PCNVat')]: t('PCNVatOnSelfInvoice'),
        [t('PCNAmount')]: t('PCNFullTaxable')
      }
    },
    {
      code: DocumentTypeCodes.EXPORT_SALE,
      name: t('ExportSale'),
      nameHe: 'ייצוא',
      type: 'Sale',
      color: '#007bff',
      description: t('PCNExportDesc'),
      useWhen: [t('PCNExportGoodsServices'), t('PCNInternationalSales')],
      rules: [t('PCNVatAlwaysZero'), t('PCNReferenceExportCustoms')],
      buildStructure: { sortCodeId: SortCodeIds.EXPORT_SALE, sortCodeName: 'מכירות ייצוא', debitAccount: t('PCNExportCustomerAccount'), creditAccount: t('PCNIncomeCard'), vat: '0', accountCaseId: t('PCNExportEntryOr999999999'), amountMeaning: t('PCNFullExportValue') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExportCustomerAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNExportEntryOr999999999'),
        [t('PCNVat')]: '000000000',
        [t('PCNAmount')]: t('PCNFullExportValue')
      }
    },
    {
      code: DocumentTypeCodes.PALESTINIAN_CUSTOMER_SALE,
      name: t('PalestinianAuthorityCustomer') || t('PalestinianCustomer'),
      nameHe: 'לקוח רשות פלסטינית',
      type: 'Sale',
      color: '#17a2b8',
      description: t('PCNPACustomerDesc'),
      useWhen: [t('PCNInvoiceToPA')],
      rules: [],
      buildStructure: { sortCodeId: SortCodeIds.PALESTINIAN_CUSTOMER_SALE, sortCodeName: 'מכירות לרשות הפלסטינית', debitAccount: t('PCNPACustomerAccount'), creditAccount: t('PCNIncomeCard'), vat: '16', accountCaseId: t('PCNCustomerVat'), amountMeaning: t('PCNFullTaxable') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNPACustomerAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNCustomerVat'),
        [t('PCNVat')]: t('PCNFullVat'),
        [t('PCNAmount')]: t('PCNFullTaxable')
      }
    },
    {
      code: DocumentTypeCodes.REGULAR_INPUT,
      name: t('RegularInput'),
      nameHe: 'תשומה רגילה',
      type: 'Input',
      color: '#28a745',
      description: t('PCNRegularInputDesc'),
      useWhen: [t('PCNStandardPurchaseInvoice'), t('PCNDomesticSupplier')],
      rules: [],
      buildStructure: { sortCodeId: t('PCNExpenseCode'), sortCodeName: 'הובלות', debitAccount: t('PCNExpenseCard'), creditAccount: t('PCNSupplierAccount'), vat: '100', accountCaseId: t('PCNSupplierVat'), amountMeaning: t('PCNGrossAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNSupplierAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNSupplierVat'),
        [t('PCNVat')]: t('PCNDeductibleVat'),
        [t('PCNAmount')]: t('PCNInvoiceNetAmount')
      }
    },
    {
      code: DocumentTypeCodes.PETTY_CASH_INPUT,
      name: t('PettyCash'),
      nameHe: 'קופה קטנה',
      type: 'Input',
      color: '#ffc107',
      description: t('PCNPettyCashDesc'),
      useWhen: [t('PCNManySmallReceipts'), t('PCNPettyCashPurchases')],
      rules: [t('PCNVatLimit2Percent')],
      buildStructure: { sortCodeId: t('PCNExpenseCodeWithPCNKOrRegularNoNumber'), sortCodeName: 'קופה קטנה', debitAccount: t('PCNExpenseCard'), creditAccount: t('PCNPettyCashAccount'), vat: '100', accountCaseId: '000000000', amountMeaning: t('PCNAggregatedPurchases') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNPettyCashAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: '000000000',
        [t('PCNDate')]: t('PCNEntryDate'),
        [t('PCNRefNumber')]: t('PCNNumberOfReceipts'),
        [t('PCNVat')]: t('PCNVatTotal'),
        [t('PCNAmount')]: t('PCNTotalNet')
      }
    },
    {
      code: DocumentTypeCodes.IMPORT_INPUT,
      name: t('ImportInput'),
      nameHe: 'יבוא (ספק חו"ל)',
      type: 'Input',
      color: '#dc3545',
      description: t('PCNImportInputDesc'),
      useWhen: [t('PCNImportVatViaCustoms'), t('PCNOverseasSupplier')],
      rules: [
        t('PCNNotSupplierInvoiceVat'),
        t('PCNVatFromImportDeclaration'),
        t('PCNSupplierVatIdNotExist'),
        t('PCNMapFromCustomsEntry'),
        t('PCNCriticalForVatReclaim')
      ],
      buildStructure: {
        sortCodeId: t('PCNSort350OrOtherWithPCNR'),
        sortCodeName: 'רשימון יבוא',
        debitAccount: `${VatCardIds.EXPENSE} (${t('PCNExpenseVatCard')})`,
        creditAccount: t('PCNForeignSupplierOrCustoms'),
        vat: '0',
        accountCaseId: t('PCNImportEntryNumber'),
        amountMeaning: t('PCNVatAmountPaidToCustoms'),
      },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNCustomsAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNImportEntryNumber'),
        [t('PCNInvoiceDate')]: t('PCNCustomsEntryDate'),
        [t('PCNRefGroup')]: t('PCNZeros'),
        [t('PCNRefNumber')]: '000000000',
        [t('PCNVatAmount')]: t('PCNVatPaidToCustoms'),
        [t('PCNSign')]: '+',
        [t('PCNAmountExclVat')]: t('PCNCustomsTaxableValue'),
        [t('PCNFutureField')]: t('PCNZeros')
      },
      important: true
    },
    {
      code: DocumentTypeCodes.PALESTINIAN_SUPPLIER_INPUT,
      name: t('PalestinianSupplier'),
      nameHe: 'ספק רשות פלסטינית',
      type: 'Input',
      color: '#17a2b8',
      description: t('PCNPASupplierDesc'),
      useWhen: [t('PCNSupplierRegisteredInPA')],
      rules: [t('PCNSameAsTButPA')],
      buildStructure: { sortCodeId: SortCodeIds.PALESTINIAN_SUPPLIER_INPUT, sortCodeName: 'קניות מרשות פלסטינית', debitAccount: t('PCNExpenseCard'), creditAccount: t('PCNPASupplierAccount'), vat: '16', accountCaseId: t('PCNSupplierVat'), amountMeaning: t('PCNGrossAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNPASupplierAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNSupplierVat'),
        [t('PCNVat')]: t('PCNDeductibleVat'),
        [t('PCNAmount')]: t('PCNInvoiceNetAmount')
      }
    },
    {
      code: DocumentTypeCodes.OTHER_DOCUMENT_INPUT,
      name: t('OtherDocumentByLaw') || t('OtherDocument'),
      nameHe: 'מסמך אחר על פי חוק',
      type: 'Input',
      color: '#6c757d',
      description: t('PCNOtherDocumentDesc'),
      useWhen: [t('PCNBankVatDocs'), t('PCNCustomsAdjustment'), t('PCNAuthorityIssuedForms')],
      rules: [],
      buildStructure: { sortCodeId: t('PCNExpenseCodeWithPCNH'), sortCodeName: 'מסמך אחר', debitAccount: t('PCNExpenseCard'), creditAccount: t('PCNOtherDocumentAccount'), vat: t('PCNRegularVat'), accountCaseId: t('PCNAsPerSHAAMRules'), amountMeaning: t('PCNPerDocument') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNOtherDocumentAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNAsPerSHAAMRules'),
        [t('PCNRefNumber')]: t('PCNIfUnknownZeros')
      }
    },
    {
      code: DocumentTypeCodes.SELF_INVOICE_INPUT,
      name: t('SelfInvoiceInput'),
      nameHe: 'חשבונית עצמית - תשומה',
      type: 'Input',
      color: '#9c27b0',
      description: t('PCNSelfInvoiceInputDesc'),
      useWhen: [t('PCNMirrorOfMSale')],
      rules: [],
      buildStructure: { sortCodeId: SortCodeIds.SELF_INVOICE_INPUT, sortCodeName: 'חשבונית עצמית תשומה', debitAccount: `${VatCardIds.EXPENSE} (${t('PCNExpenseVatCard')})`, creditAccount: `${VatCardIds.INCOME} (${t('PCNIncomeVatCard')})`, vat: '0', accountCaseId: t('PCNSupplierVat'), amountMeaning: t('PCNTheVatAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNCustomerAccount')
      },
      fields: {
        [t('PCNCounterpartyVat')]: t('PCNCustomerVat'),
        [t('PCNVat')]: t('PCNDeductibleVat'),
        [t('PCNAmount')]: t('PCNFullTaxable')
      }
    },
    {
      code: 'C+M',  // Display label for C_PLUS_M pair (DocumentTypeCodes.C_PLUS_M = 'B')
      name: t('CPlusM'),
      nameHe: 'C+M זוג חשבונית עצמית',
      type: 'Composite',
      color: '#9c27b0',
      description: t('PCNCPlusMDesc'),
      useWhen: [t('PCNCPlusMUseWhen')],
      rules: [t('PCNCPlusMRules')],
      buildStructure: { sortCodeId: SortCodeIds.C_PLUS_M, sortCodeName: 'חשבונית עצמית', debitAccount: `3 (${t('PCNExpenseVatCard')}) או 2 (${t('PCNExpenseEquipmentVatCard')})`, creditAccount: `1 (${t('PCNIncomeVatCard')})`, vat: '0', accountCaseId: t('PCNCustomerOrSupplierCaseId'), amountMeaning: t('PCNVatAmount') },
      journalEntry: {
        sortCode: t('PCNSortCodeAsTransactionType'),
        debitAccount: t('PCNExpenseAccount'),
        creditAccount: t('PCNIncomeAccount')
      },
      fields: {}
    },
    {
      code: 'Offset',
      name: t('TechnicalOffset'),
      nameHe: 'קיזוז טכני (זיכויים)',
      type: 'Composite',
      color: '#ff6b6b',
      description: t('PCNTechnicalOffsetDesc'),
      useWhen: [t('PCNNegativeAmounts'), t('PCNCreditNotes'), t('PCNRefunds')],
      rules: [t('PCNAutoGenerated'), t('PCNOffsetsNegativeValues')],
      buildStructure: { sortCodeId: '608', sortCodeName: 'חשבונית עצמית', debitAccount: `3 (${t('PCNExpenseVatCard')}) או 2 (${t('PCNExpenseEquipmentVatCard')})`, creditAccount: `1 (${t('PCNIncomeVatCard')})`, vat: '0', accountCaseId: t('PCNOwnerCaseId'), amountMeaning: t('PCNAbsoluteNegativeAmount') },
      journalEntry: {
        sortCode: '608 (חשבונית עצמית)',
        debitAccount: 'מע"מ תשומות (3) או ציוד (2)',
        creditAccount: 'מע"מ עסקאות (1)'
      },
      fields: {
        'סכום': 'ערך מוחלט של הסכום השלילי',
        'ח.פ/ע.מ': 'מספר העוסק עצמו',
        'תיאור': 'קיזוז זיכוי - מסמך [מספר]'
      }
    }
  ];

  const saleTypes = transactionTypes.filter(t => t.type === 'Sale');
  const inputTypes = transactionTypes.filter(t => t.type === 'Input' || t.type === 'Composite');
  
  // Helper function to translate transaction type
  const getTranslatedType = (type) => {
    return type === 'Sale' ? t('PCNTypeSale') : t('PCNTypeInput');
  };

  const renderTransactionCard = (transaction) => (
    <Accordion key={transaction.code} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Chip
            label={transaction.code}
            sx={{
              backgroundColor: transaction.color,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px',
              minWidth: '40px'
            }}
          />
          <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
            {transaction.name}
          </Typography>
          <Typography sx={{ color: '#666', fontSize: '14px' }}>
            {transaction.nameHe}
          </Typography>
          {transaction.important && (
            <Chip label={t('PCNImportant')} color="error" size="small" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            {transaction.description}
          </Typography>

          {/* How to Build - Exact journal entry structure (as in system) */}
          {transaction.buildStructure && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #81c784' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                {t('PCNHowToBuild', { defaultValue: 'How to Build' })}:
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 140 }}>{t('PCNSortCode')} </TableCell>
                      <TableCell>{transaction.buildStructure.sortCodeId} — {transaction.buildStructure.sortCodeName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('PCNDebitAccount')}</TableCell>
                      <TableCell>{transaction.buildStructure.debitAccount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('PCNCreditAccount')}</TableCell>
                      <TableCell>{transaction.buildStructure.creditAccount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('vat')} %</TableCell>
                      <TableCell>{transaction.buildStructure.vat}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('PCNAccountCaseId', { defaultValue: ' (ח.פ/ע.מ)' })}</TableCell>
                      <TableCell>{transaction.buildStructure.accountCaseId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('PCNAmountMeaning', { defaultValue: 'Amount' })}</TableCell>
                      <TableCell>{transaction.buildStructure.amountMeaning}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Journal Entry Structure (legacy/summary) */}
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('PCNJournalEntryStructure')}:
            </Typography>
            <ul style={{ margin: 10, paddingLeft: '20px' }}>
              <li>
                <Typography variant="body2">
                  <strong>{t('PCNSortCode')}:</strong> {transaction.journalEntry?.sortCode || t('PCNSortCodeAsTransactionType')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{t('PCNDebitAccount')}:</strong> {transaction.journalEntry?.debitAccount || (transaction.type === 'Sale' ? t('PCNClientAccount') : t('PCNExpenseAccount'))}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{t('PCNCreditAccount')}:</strong> {transaction.journalEntry?.creditAccount || (transaction.type === 'Sale' ? t('PCNIncomeAccount') : t('PCNSupplierAccount'))}
                </Typography>
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#1976d2' }}>
              {transaction.type === 'Sale' 
                ? t('PCNIncomeNote') 
                : t('PCNExpenseNote')}
            </Typography>
          </Box>

          {transaction.useWhen.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('PCNUseWhen')}:
              </Typography>
              <ul style={{ margin: 10, paddingLeft: '20px' }}>
                {transaction.useWhen.map((item, idx) => (
                  <li key={idx}><Typography variant="body2">{item}</Typography></li>
                ))}
              </ul>
            </Box>
          )}

          {transaction.rules.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: transaction.important ? '#dc3545' : '#333' }}>
                {t('PCNRules')}:
              </Typography>
              <ul style={{ margin: 10, paddingLeft: '20px' }}>
                {transaction.rules.map((rule, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" sx={{ color: transaction.important ? '#dc3545' : '#333' }}>
                      {rule}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('PCNFieldValues')}:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('PCNField')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('PCNValue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(transaction.fields).map(([field, value]) => (
                    <TableRow key={field}>
                      <TableCell>{field}</TableCell>
                      <TableCell>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderOverview = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('PCN874TransactionTypesOverview')}
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('PCN874Description')}
        </Typography>
      </Paper>

      {/* Journal Entry Explanation */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          {t('PCNJournalEntryExplanation')}:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('PCNEachTransactionContains')}
        </Typography>
        <ul style={{ margin: 10, paddingLeft: '20px' }}>
          <li><Typography variant="body2"><strong>{t('PCNSortCode')}:</strong> {t('PCNSortCodeExplanation')}</Typography></li>
          <li><Typography variant="body2"><strong>{t('PCNDebitAccount')}:</strong> {t('PCNDebitAccountExplanation')}</Typography></li>
          <li><Typography variant="body2"><strong>{t('PCNCreditAccount')}:</strong> {t('PCNCreditAccountExplanation')}</Typography></li>
        </ul>
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          <strong>{t('PCNImportant')}:</strong> {t('PCNExpenseCreditIsSupplier')} {t('PCNIncomeDebitIsClient')}
        </Typography>
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        {t('PCNQuickReferenceTable')}:
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('PCNCode')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('PCNType')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('PCNDescription')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('PCNHebrewName')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactionTypes.map((tx) => (
              <TableRow key={tx.code} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                <TableCell>
                  <Chip
                    label={tx.code}
                    size="small"
                    sx={{ backgroundColor: tx.color, color: 'white', fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>{getTranslatedType(tx.type)}</TableCell>
                <TableCell>{tx.name}</TableCell>
                <TableCell>{tx.nameHe}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107' }} >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          ⚠️ {t('PCNImportantNotes')}:
        </Typography>
        <ul style={{ margin: 5, paddingLeft: '20px' }}>
          <li><Typography variant="body2">{t('PCNTypeRFromCustoms')}</Typography></li>
          <li><Typography variant="body2">{t('PCNZeroPaddedFields')}</Typography></li>
          <li><Typography variant="body2">{t('PCNAllFieldsMandatory')}</Typography></li>
        </ul>
      </Paper>
    </Box>
  );

  return (
    <XtramModal
      open={open}
      onClose={onClose}
      title={t('PCN874TransactionTypesDocumentation')}
      direction={direction}
      expanded
      tabs={[
        { key: 0, label: t('PCNOverview') },
        { key: 1, label: t('PCNSalesTypes'), count: saleTypes.length },
        { key: 2, label: t('PCNInputTypes'), count: inputTypes.length },
      ]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footer={(
        <>
          <div style={{ flex: '1 1 auto' }} />
          <XtramButton variant="secondary" onClick={onClose}>{t('Close')}</XtramButton>
        </>
      )}
    >
      {activeTab === 0 && renderOverview()}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('PCNSalesTransactionTypes')}
          </Typography>
          {saleTypes.map(renderTransactionCard)}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('PCNInputTransactionTypes')}
          </Typography>
          {inputTypes.map(renderTransactionCard)}
        </Box>
      )}
    </XtramModal>
  );
};

export default PCNDocumentationModal;
